import React, { useState, useCallback, useEffect, useMemo } from 'react'

import Carousel from 'nuka-carousel'

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Button,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, bool } from 'prop-types'

import DesignerModel from 'Pods/Shared/PrintModels/components/DesignerModel'

import CarouselProgress from './CarouselProgress'

function ModelCarousel({ models, showControls = false }) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const wrapAround = false

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const debounce = useCallback((fn, delay) => {
    let timeoutId
    return function debouncedFn(...args) {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => fn.apply(this, args), delay)
    }
  }, [])

  const handleSlideChange = (newIndex) => {
    setCurrentSlide(newIndex)
    setIsTransitioning(false)
  }

  const goToSlide = useCallback(
    debounce((index) => {
      setCurrentSlide(index)
      setIsTransitioning(true)
    }, 100),
    []
  )

  // Built once per `models` so a swipe (state change) does not recreate every slide.
  const slides = useMemo(
    () => models?.map((model) => (
      <div key={model.id} className="model-carousel__slide">
        <DesignerModel {...model} />
      </div>
    )),
    [models]
  )

  const handlePrevious = () => {
    if (isTransitioning) return
    let newIndex = currentSlide
    if (currentSlide > 0) {
      newIndex = currentSlide - 1
    } else if (wrapAround) {
      newIndex = models.length - 1
    }
    goToSlide(newIndex)
  }

  const handleNext = () => {
    if (isTransitioning) return
    let newIndex = currentSlide
    if (currentSlide < models.length - 1) {
      newIndex = currentSlide + 1
    } else if (wrapAround) {
      newIndex = 0
    }
    goToSlide(newIndex)
  }

  if (!models || !Array.isArray(models) || models.length === 0) {
    return null
  }

  // Render initial state for SSR
  if (!isMounted) {
    return (
      <div className="model-carousel">
        <div className="model-carousel__slide">
          <DesignerModel {...models[0]} lazyLoad />
        </div>
        {showControls && (
          <div className="model-carousel__controls">
            <Button type="secondary" size="small" disabled customClass="model-carousel__button">
              <ChevronLeftIcon width="16" height="16" />
            </Button>
            <div className="model-carousel__progress">
              <CarouselProgress currentSlide={0} totalSlides={models.length} />
            </div>
            <Button type="secondary" size="small" disabled customClass="model-carousel__button">
              <ChevronRightIcon width="16" height="16" />
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="model-carousel">
      <Carousel
        slideIndex={currentSlide}
        afterSlide={handleSlideChange}
        slidesToShow={1.1}
        cellSpacing={16}
        withoutControls
        wrapAround={false}
        dragging={!isTransitioning}
        swiping={!isTransitioning}
        enableKeyboardControls={!isTransitioning}
        cellAlign="left"
        slideWidth="85%"
        speed={150}
        defaultControlsConfig={{
          pagingDotsStyle: { display: 'none' },
        }}
      >
        {slides}
      </Carousel>

      {showControls && (
        <div className="model-carousel__controls">
          <Button
            type="secondary"
            size="small"
            onClick={handlePrevious}
            aria-label="Previous slide"
            customClass="model-carousel__button"
            disabled={(currentSlide === 0 && !wrapAround) || isTransitioning}
          >
            <ChevronLeftIcon width="16" height="16" />
          </Button>

          <div className="model-carousel__progress">
            <CarouselProgress currentSlide={currentSlide} totalSlides={models.length} />
          </div>

          <Button
            type="secondary"
            size="small"
            onClick={handleNext}
            aria-label="Next slide"
            customClass="model-carousel__button"
            disabled={(currentSlide === models.length - 1 && !wrapAround) || isTransitioning}
          >
            <ChevronRightIcon width="16" height="16" />
          </Button>
        </div>
      )}
    </div>
  )
}

ModelCarousel.propTypes = {
  models: array.isRequired,
  showControls: bool,
}

export default ModelCarousel
